import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { FloatingChatSnapshot } from "../components/direct/interface/scraii-ai-interface";
import { NavigationService } from "./navigation.service";
import { FilterService } from "./filter.service";
import { SessionService } from "./session.service";
import { AI_MODULE_KEY, DashboardService } from "./dashboard.service";

export type FloatingChatNavigationAction =
  | { type: "none" }
  | { type: "clearAllAndReset" }
  | { type: "restore"; snapshot: FloatingChatSnapshot }
  | { type: "resetLive" };

export interface DashboardPageMeta {
  page_key: number;

  page_nm: string;
}

export interface ActiveDashboardPages {
  dashboardKey: number | null;

  pages: DashboardPageMeta[];

  activeTabIndex: number;
}

export const FLOATING_CHAT_GLOBAL_LISTING_KEY = "global-listing";

export const FLOATING_CHAT_ENABLED_CLIENT_KEY = 2001; // sanofi

const MAX_CACHED_PAGES = 20;

const PATIENT_LIST_PAGE_NAME = "patientlist";
const PAT_THERAPY_PAGE_NAMES = new Set([
  "pwt",
  "patientawaitingtherapy",
  "patientsawaitingtherapy",
  "pattherapy",
]);

@Injectable({
  providedIn: "root",
})
export class FloatingChatStateService {
  private readonly snapshots = new Map<string, FloatingChatSnapshot>();

  private readonly accessOrder: string[] = [];

  private activePages: ActiveDashboardPages | null = null;

  private readonly pageContextPublishedSubject = new Subject<void>();

  readonly onPageContextPublished =
    this.pageContextPublishedSubject.asObservable();

  constructor(
    private router: Router,

    private navigationService: NavigationService,

    private filterService: FilterService,

    private sessionService: SessionService,

    private dashboardService: DashboardService,
  ) {}

  isScraiiAiRoute(url: string): boolean {
    const path = url.split("?")[0].split("#")[0];

    return /^\/direct\/ai(\/|$)/i.test(path);
  }

  isDashboardRoute(url: string): boolean {
    const path = url.split("?")[0].split("#")[0];

    return path.startsWith("/dashboard");
  }

  isOnDashboardPageRoute(url: string): boolean {
    const path = url.split("?")[0].split("#")[0];

    return /\/dashboard\/[^/]+\/page(?:\/|$)/i.test(path);
  }

  isModuleListingRoute(url: string): boolean {
    const segments = this.getPathSegments(url);

    return segments[0] === "dashboard" && segments.length <= 2;
  }

  isInsideWorkRoute(url: string): boolean {
    return (
      this.isOnDashboardPageRoute(url) ||
      !!this.resolveCardNameFromUrl(url) ||
      !!this.resolveDirectRouteFromUrl(url)
    );
  }

  resolveCurrentClientKey(): number | null {
    const fromFilter =
      this.filterService.client_key ?? this.filterService.baseQuery?.client_key;

    if (fromFilter != null && fromFilter !== "") {
      const parsed = Number(fromFilter);
      return Number.isFinite(parsed) ? parsed : null;
    }

    const client = this.sessionService.getClient();
    if (client?.client_key == null || client.client_key === "") {
      return null;
    }

    const parsed = Number(client.client_key);
    return Number.isFinite(parsed) ? parsed : null;
  }

  shouldShowFloatingChat(url: string): boolean {
    return (
      this.isInsideWorkRoute(url) &&
      this.resolveCurrentClientKey() === FLOATING_CHAT_ENABLED_CLIENT_KEY &&
      this.dashboardService.hasModuleAccess(AI_MODULE_KEY)
    );
  }

  resolvePageContextKey(url: string): string {
    return this.resolveWorkContextKey(url);
  }

  resolveWorkContextKey(url: string): string {
    if (!this.isDashboardRoute(url)) {
      return "off-dashboard";
    }

    if (this.isModuleListingRoute(url)) {
      return FLOATING_CHAT_GLOBAL_LISTING_KEY;
    }

    if (this.isOnDashboardPageRoute(url)) {
      const dsKey = this.resolveDsKeyFromUrl(url);

      return dsKey ? `page:${dsKey}` : FLOATING_CHAT_GLOBAL_LISTING_KEY;
    }

    const direct = this.resolveDirectRouteFromUrl(url);

    if (direct) {
      return `direct:${direct.module}:${this.normalizePageName(direct.component)}`;
    }

    const cardName = this.resolveCardNameFromUrl(url);

    if (cardName) {
      const module = this.resolveModuleFromUrl(url) ?? "unknown";

      return `card:${module}:${this.normalizePageName(cardName)}`;
    }

    return FLOATING_CHAT_GLOBAL_LISTING_KEY;
  }

  resolveDashboardKey(url?: string): number | null {
    if (url) {
      const fromUrl = this.resolveDashboardKeyFromUrl(url);

      if (fromUrl != null) {
        return fromUrl;
      }
    }

    return (
      this.filterService.baseQuery?.dashboard_key ??
      this.filterService.dashboardKey ??
      null
    );
  }

  resolveDashboardBrandKey(url: string): number | null {
    if (!this.isOnDashboardPageRoute(url)) {
      return null;
    }

    return this.filterService.baseQuery?.brand_key ?? null;
  }

  publishActiveDashboardPages(context: ActiveDashboardPages): void {
    this.activePages = context;

    this.pageContextPublishedSubject.next();
  }

  clearActiveDashboardPages(): void {
    this.activePages = null;
  }

  resolvePageName(url: string): string {
    if (!this.isDashboardRoute(url)) {
      return "";
    }

    const cardName = this.resolveCardNameFromUrl(url);

    if (cardName) {
      const mapped = this.mapPageNameToApiValue(cardName);
      if (mapped) {
        return mapped;
      }
    }

    const direct = this.resolveDirectRouteFromUrl(url);

    if (direct) {
      const mapped = this.mapPageNameToApiValue(direct.component);
      if (mapped) {
        return mapped;
      }
    }

    if (this.isOnDashboardPageRoute(url)) {
      const tabId =
        this.activePages?.activeTabIndex ?? this.resolveTabIdFromUrl(url) ?? 0;

      const pageNm = this.activePages?.pages[tabId]?.page_nm;
      const mapped = this.mapPageNameToApiValue(pageNm);
      if (mapped) {
        return mapped;
      }
    }

    return this.mapPageNameToApiValue(this.resolveNavbarDashboardName());
  }

  isPageContextKey(key: string): boolean {
    return key.startsWith("page:");
  }

  isWorkContextKey(key: string): boolean {
    return (
      key.startsWith("page:") ||
      key.startsWith("card:") ||
      key.startsWith("direct:")
    );
  }

  isPersistableContextKey(key: string): boolean {
    return (
      key === FLOATING_CHAT_GLOBAL_LISTING_KEY || this.isWorkContextKey(key)
    );
  }

  isGlobalListingKey(key: string): boolean {
    return key === FLOATING_CHAT_GLOBAL_LISTING_KEY;
  }

  getSnapshot(key: string): FloatingChatSnapshot | undefined {
    return this.snapshots.get(key);
  }

  saveSnapshot(key: string, snapshot: FloatingChatSnapshot): void {
    if (!this.isPersistableContextKey(key)) {
      return;
    }

    this.snapshots.set(key, snapshot);

    this.touchAccessOrder(key);

    this.evictIfNeeded();
  }

  deleteSnapshot(key: string): void {
    this.snapshots.delete(key);

    const idx = this.accessOrder.indexOf(key);

    if (idx >= 0) {
      this.accessOrder.splice(idx, 1);
    }
  }

  clearAll(): void {
    this.snapshots.clear();

    this.accessOrder.length = 0;
  }

  /**

   * Saves the live snapshot when leaving a persistable context, then returns the action

   * the host component should apply to its ScraiiAi instance.

   */

  handlePageContextTransition(
    prevKey: string | null,

    nextKey: string,

    liveSnapshot: FloatingChatSnapshot | null,
  ): FloatingChatNavigationAction {
    if (
      prevKey &&
      prevKey !== nextKey &&
      liveSnapshot &&
      this.isPersistableContextKey(prevKey)
    ) {
      this.saveSnapshot(prevKey, liveSnapshot);
    }

    if (nextKey === prevKey) {
      return { type: "none" };
    }

    // Module listing — persist chat across module switches.

    if (nextKey === FLOATING_CHAT_GLOBAL_LISTING_KEY) {
      if (prevKey && this.isWorkContextKey(prevKey)) {
        const cached = this.getSnapshot(FLOATING_CHAT_GLOBAL_LISTING_KEY);

        if (cached) {
          return { type: "restore", snapshot: cached };
        }

        return { type: "none" };
      }

      if (prevKey === "off-dashboard") {
        const cached = this.getSnapshot(FLOATING_CHAT_GLOBAL_LISTING_KEY);

        if (cached) {
          return { type: "restore", snapshot: cached };
        }
      }

      return { type: "none" };
    }

    // Entering a work page (tabbed, card, or direct).

    if (this.isWorkContextKey(nextKey)) {
      const enteringFromListing =
        prevKey === FLOATING_CHAT_GLOBAL_LISTING_KEY ||
        prevKey === "off-dashboard" ||
        !prevKey ||
        !this.isWorkContextKey(prevKey);

      if (enteringFromListing) {
        return { type: "resetLive" };
      }

      const cached = this.getSnapshot(nextKey);

      if (cached) {
        return { type: "restore", snapshot: cached };
      }

      return { type: "resetLive" };
    }

    return { type: "none" };
  }

  /**

   * Returns clearAllAndReset when entering a work route with a different dashboard_key.

   */

  handleDashboardSwitch(
    prevDashboardKey: number | null,

    nextDashboardKey: number | null,

    _prevPageContextKey: string | null,

    nextPageContextKey: string,
  ): FloatingChatNavigationAction {
    const nextIsWork = this.isWorkContextKey(nextPageContextKey);

    if (
      nextIsWork &&
      prevDashboardKey != null &&
      nextDashboardKey != null &&
      prevDashboardKey !== nextDashboardKey
    ) {
      this.clearAll();

      return { type: "clearAllAndReset" };
    }

    return { type: "none" };
  }

  private getPathSegments(url: string): string[] {
    const path = url.split("?")[0].split("#")[0];

    return path.split("/").filter(Boolean);
  }

  private resolveModuleFromUrl(url: string): string | null {
    const segments = this.getPathSegments(url);

    if (segments[0] !== "dashboard" || segments.length < 2) {
      return null;
    }

    return decodeURIComponent(segments[1]);
  }

  private resolveDirectRouteFromUrl(
    url: string,
  ): { module: string; component: string } | null {
    const segments = this.getPathSegments(url);

    if (
      segments[0] !== "dashboard" ||
      segments.length < 4 ||
      segments[2].toLowerCase() !== "direct"
    ) {
      return null;
    }

    return {
      module: decodeURIComponent(segments[1]),

      component: decodeURIComponent(segments[3]),
    };
  }

  private resolveDashboardKeyFromUrl(url: string): number | null {
    const decrypted = this.decryptQueryParamsFromUrl(url);

    if (!decrypted) {
      return null;
    }

    const dsKey = decrypted["ds_key"];

    if (dsKey == null || dsKey === "") {
      return null;
    }

    const parsed = Number(dsKey);

    return Number.isFinite(parsed) ? parsed : null;
  }

  private resolveDsKeyFromUrl(url: string): string | null {
    if (!this.isOnDashboardPageRoute(url)) {
      return null;
    }

    const decrypted = this.decryptQueryParamsFromUrl(url);

    if (!decrypted) {
      return null;
    }

    const dsKey = decrypted["ds_key"];

    return dsKey != null ? String(dsKey) : null;
  }

  private resolveTabIdFromUrl(url: string): number | null {
    const decrypted = this.decryptQueryParamsFromUrl(url);

    if (!decrypted) {
      return null;
    }

    const tabId = decrypted["tab_id"];

    if (tabId == null || tabId === "") {
      return null;
    }

    const parsed = Number(tabId);

    return Number.isFinite(parsed) ? parsed : null;
  }

  private resolveCardNameFromUrl(url: string): string | null {
    const segments = this.getPathSegments(url);

    if (segments[0] !== "dashboard" || segments.length < 3) {
      return null;
    }

    const segment = decodeURIComponent(segments[2]);

    if (
      segment.toLowerCase() === "page" ||
      segment.toLowerCase() === "direct"
    ) {
      return null;
    }

    return segment;
  }

  private decryptQueryParamsFromUrl(url: string): Record<string, any> | null {
    try {
      const tree = this.router.parseUrl(url);

      const queryParams: Record<string, string> = {};

      tree.queryParamMap.keys.forEach((key) => {
        const value = tree.queryParamMap.get(key);

        if (value != null) {
          queryParams[key] = value;
        }
      });

      if (Object.keys(queryParams).length === 0) {
        return null;
      }

      return this.navigationService.decryptData(queryParams);
    } catch {
      return null;
    }
  }

  private normalizePageName(name: string | null | undefined): string {
    return name?.toString().replace(/\s+/g, "").toLowerCase() ?? "";
  }

  private mapPageNameToApiValue(
    name: string | null | undefined,
  ): string {
    const normalized = this.normalizePageName(name);

    if (!normalized) {
      return "";
    }

    if (normalized === PATIENT_LIST_PAGE_NAME) {
      return "patient_list";
    }

    if (PAT_THERAPY_PAGE_NAMES.has(normalized)) {
      return "pat_therapy";
    }

    return "";
  }

  private resolveNavbarDashboardName(): string | null {
    try {
      const raw = sessionStorage.getItem("navbar_page_nm");
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);
      return parsed?.dashboard_nm || parsed?.work_nm || null;
    } catch {
      return null;
    }
  }

  private touchAccessOrder(key: string): void {
    const idx = this.accessOrder.indexOf(key);

    if (idx >= 0) {
      this.accessOrder.splice(idx, 1);
    }

    this.accessOrder.push(key);
  }

  private evictIfNeeded(): void {
    while (this.accessOrder.length > MAX_CACHED_PAGES) {
      const oldest = this.accessOrder.shift();

      if (oldest) {
        this.snapshots.delete(oldest);
      }
    }
  }
}
